import { useState } from "react";
import { Link } from "wouter";
import { trpc } from "@/lib/trpc";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader } from "@/components/ui/card";
import { Mail, ArrowLeft, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";

export default function ForgotPasswordPage() {
  const [email, setEmail] = useState("");
  const [sent, setSent] = useState(false);

  const requestReset = trpc.auth.requestPasswordReset.useMutation({
    onSuccess: () => setSent(true),
    onError: (err) => toast.error(err.message),
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!email) return;
    requestReset.mutate({ email });
  };

  return (
    <div
      className="min-h-screen flex items-center justify-center p-4"
      style={{
        background: "linear-gradient(135deg, #1a0a00 0%, #2d1200 40%, #1a0a00 100%)",
      }}
    >
      {/* Decorative circles */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute -top-32 -right-32 w-96 h-96 rounded-full opacity-10" style={{ background: "radial-gradient(circle, #c97d2e, transparent)" }} />
        <div className="absolute -bottom-32 -left-32 w-96 h-96 rounded-full opacity-10" style={{ background: "radial-gradient(circle, #c97d2e, transparent)" }} />
      </div>

      <div className="w-full max-w-sm relative z-10">
        {/* Logo */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-16 h-16 rounded-2xl mb-4 shadow-lg" style={{ background: "linear-gradient(135deg, #c97d2e, #e8a84e)" }}>
            <span className="text-white font-bold text-2xl font-serif">B</span>
          </div>
          <h1 className="text-2xl font-bold text-white font-serif">Beleza & Bronze</h1>
          <p className="text-sm mt-1" style={{ color: "#c97d2e" }}>Painel Administrativo</p>
        </div>

        <Card className="border-0 shadow-2xl" style={{ background: "rgba(255,255,255,0.97)" }}>
          <CardHeader className="pb-2 pt-6 px-6">
            <h2 className="text-xl font-semibold text-gray-800 text-center">
              {sent ? "E-mail enviado!" : "Esqueci minha senha"}
            </h2>
            <p className="text-sm text-gray-500 text-center mt-1">
              {sent
                ? "Verifique sua caixa de entrada e siga as instruções."
                : "Informe seu e-mail para receber o link de redefinição."}
            </p>
          </CardHeader>
          <CardContent className="px-6 pb-6 pt-4">
            {sent ? (
              <div className="flex flex-col items-center gap-4 py-4">
                <CheckCircle2 className="w-14 h-14" style={{ color: "#c97d2e" }} />
                <p className="text-sm text-gray-600 text-center">
                  Se o e-mail <strong>{email}</strong> estiver cadastrado, você receberá um link válido por <strong>1 hora</strong>.
                </p>
                <Link href="/login">
                  <Button variant="outline" className="w-full mt-2">
                    <ArrowLeft className="w-4 h-4 mr-2" />
                    Voltar ao login
                  </Button>
                </Link>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="space-y-1.5">
                  <Label htmlFor="email" className="text-gray-700 font-medium text-sm">E-mail</Label>
                  <div className="relative">
                    <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                    <Input
                      id="email"
                      type="email"
                      placeholder="seu@email.com"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      className="pl-9 h-11 border-gray-200 focus:border-amber-500 focus:ring-amber-500/20"
                      required
                    />
                  </div>
                </div>

                <Button
                  type="submit"
                  className="w-full h-11 font-semibold text-white shadow-md"
                  style={{ background: "linear-gradient(135deg, #c97d2e, #e8a84e)" }}
                  disabled={requestReset.isPending}
                >
                  {requestReset.isPending ? "Enviando..." : "Enviar link de redefinição"}
                </Button>

                <Link href="/login">
                  <Button variant="ghost" className="w-full text-gray-500 hover:text-gray-700">
                    <ArrowLeft className="w-4 h-4 mr-2" />
                    Voltar ao login
                  </Button>
                </Link>
              </form>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
